import React from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Sparkles, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Landing() {
  const { brand } = useOutletContext();
  const { brand: key } = useParams();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.5)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  const heroBg = dark
    ? "https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?q=80&w=1400&auto=format&fit=crop"
    : "https://images.unsplash.com/photo-1576092768241-dec231879fc3?q=80&w=1400&auto=format&fit=crop";

  const categories = dark
    ? [
        { label: "Single Estate", url: "https://images.unsplash.com/photo-1597481499750-3e6b22637e12?q=80&w=800&auto=format&fit=crop" },
        { label: "Flavoured", url: "https://images.unsplash.com/photo-1571934811356-5cc061b6821f?q=80&w=800&auto=format&fit=crop" },
        { label: "Specialty", url: "https://images.unsplash.com/photo-1547825407-2d060104b7f8?q=80&w=800&auto=format&fit=crop" },
        { label: "Accessories", url: "https://images.unsplash.com/photo-1481833761820-0509d3217039?q=80&w=800&auto=format&fit=crop" },
      ]
    : [
        { label: "Tea Leaves", url: "https://images.unsplash.com/photo-1582793988951-9aed550cbe14?q=80&w=800&auto=format&fit=crop" },
        { label: "Dairy & Milk", url: "https://images.unsplash.com/photo-1563636619-e9143da7973b?q=80&w=800&auto=format&fit=crop" },
        { label: "Sugar", url: "https://images.unsplash.com/photo-1610137350761-9c0e7c1cf2e8?q=80&w=800&auto=format&fit=crop" },
        { label: "Snacks & More", url: "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?q=80&w=800&auto=format&fit=crop" },
      ];

  const headline = dark ? "Single-estate luxury, in every leaf." : "Brew your business with honest essentials.";
  const sub = dark
    ? "Hand-selected leaves, accessories, and rituals — curated for tea bars, hotels, and connoisseurs. Live tracking and instant GST invoices included."
    : "Tea, milk, sugar and the small things that make every cup count — delivered to your outlet, with live tracking and instant GST invoices.";

  return (
    <div style={{ color: fg }}>
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="overline mb-4" style={{ color: subtle }} data-testid="hero-overline">
              {brand.name} • A Big Billion brand
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
              {headline.split(" ").slice(0, -2).join(" ")}{" "}
              <span style={{ color: brand.primary }}>{headline.split(" ").slice(-2).join(" ")}</span>
            </h1>
            <p className="mt-6 text-lg max-w-xl leading-relaxed" style={{ color: muted }}>
              Wholesale supplies — available exclusively to our authorised franchise partners. Live tracking, instant GST invoices and direct sync to Zoho Books.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={`/b/${key}/login`}>
                <Button data-testid="hero-signin-btn" size="lg" className="text-white px-7 py-6 text-base" style={{ background: brand.primary }}>
                  Sign in to order <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="mailto:admin@bigbillionfnb.com" data-testid="hero-partner-mailto"
                className="text-sm font-semibold hover:underline" style={{ color: fg }}>
                Become a franchise partner →
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm" style={{ color: muted }}>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: brand.accent }} /> Verified suppliers</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4" style={{ color: brand.primary }} /> 24–48 hr delivery</div>
              <div className="flex items-center gap-2"><Receipt className="w-4 h-4" style={{ color: brand.accent }} /> Auto GST invoices</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ border: `1px solid ${cardBorder}` }}>
              <img src={heroBg} alt={brand.name} className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-xl shadow-lg p-4 hidden md:flex items-center gap-3"
              style={{ background: dark ? "#1a1612" : "white", border: `1px solid ${cardBorder}` }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: brand.accent + "33" }}>
                <Sparkles className="w-5 h-5" style={{ color: brand.accent }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: subtle }}>Trusted by</p>
                <p className="font-heading font-bold">1,200+ outlets</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline" style={{ color: subtle }}>Catalog</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-2">
              {dark ? "A curated specialty selection." : "Everything a tea stall needs."}
            </h2>
          </div>
          <Link to={`/b/${key}/shop`} className="text-sm font-semibold hover:underline" style={{ color: brand.primary }} data-testid="see-all-link">
            See all →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => (
            <Link key={c.label} to={`/b/${key}/shop`} data-testid={`category-card-${i}`}
              className="group relative rounded-xl overflow-hidden h-56" style={{ border: `1px solid ${cardBorder}` }}>
              <img src={c.url} alt={c.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 font-heading font-semibold text-white text-lg">{c.label}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-20" style={{ background: dark ? "rgba(255,255,255,0.03)" : "#F5F2EB" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline" style={{ color: subtle }}>How it works</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-2 mb-12 max-w-2xl">
            From your dashboard to your counter — three honest steps.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Browse & order", d: "Pick from our curated catalog and add to cart in seconds." },
              { n: "02", t: "Pay securely", d: "Razorpay handles your card, UPI, netbanking — all on one screen." },
              { n: "03", t: "Track & receive", d: "Live delivery status and an instant PDF invoice for every order." },
            ].map((s) => (
              <div key={s.n} className="p-8 rounded-xl hover:shadow-md transition-shadow"
                style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                <p className="font-heading text-5xl font-light" style={{ color: brand.primary }}>{s.n}</p>
                <h3 className="font-heading font-semibold text-xl mt-4">{s.t}</h3>
                <p className="mt-2 leading-relaxed" style={{ color: muted }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
